import React, { useState, useEffect } from 'react';
import { NockchainGrpcService, GrpcError } from '../services/grpc';

interface Metrics {
  cacheHeight: number;
  heaviestHeight: number;
  cacheLowestHeight: number;
  cacheSpan: number;
  cacheCoverageRatio: number;
  refreshAgeSeconds: number;
  backfillAgeSeconds: number;
  seedReady: boolean;
  seedTimeSeconds: number;
  backfillResumeHeight: number;
  cacheAgeSeconds: number;
  refreshSuccessCount: number;
  refreshErrorCount: number;
  backfillSuccessCount: number;
  backfillErrorCount: number;
  getBlocksP50Ms: number;
  getBlocksP90Ms: number;
  getBlocksP99Ms: number;
  getBlockDetailsP50Ms: number;
  getBlockDetailsP90Ms: number;
  getBlockDetailsP99Ms: number;
}

const MetricsDashboard: React.FC = () => {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const fetchMetrics = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await NockchainGrpcService.getExplorerMetrics();
      if (response.result?.metrics) {
        setMetrics(response.result.metrics);
      }
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh metrics every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
    return `${(seconds / 3600).toFixed(1)}h`;
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  return (
    <div>
      <div className="service-section">
        <h2>Explorer Metrics</h2>
        <p>Real-time statistics about the blockchain explorer and cache performance.</p>

        <button
          className="btn"
          onClick={fetchMetrics}
          disabled={loading}
          style={{ marginBottom: '2rem' }}
        >
          {loading ? 'Refreshing...' : 'Refresh Metrics'}
        </button>

        {error && (
          <div className="response-section error-response">
            <h3>Error</h3>
            <pre>{error}</pre>
          </div>
        )}

        {metrics && (
          <div className="metrics-grid">
            {/* Height Metrics */}
            <div className="metric-card">
              <h3>Block Heights</h3>
              <div className="metric-value">{formatNumber(metrics.cacheHeight)}</div>
              <div className="metric-description">Cache Height</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                Heaviest: {formatNumber(metrics.heaviestHeight)}<br/>
                Lowest: {formatNumber(metrics.cacheLowestHeight)}
              </div>
            </div>

            {/* Cache Coverage */}
            <div className="metric-card">
              <h3>Cache Coverage</h3>
              <div className="metric-value">{(metrics.cacheCoverageRatio * 100).toFixed(1)}%</div>
              <div className="metric-description">Coverage Ratio</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                Span: {formatNumber(metrics.cacheSpan)} blocks
              </div>
            </div>

            {/* Age Metrics */}
            <div className="metric-card">
              <h3>Data Freshness</h3>
              <div className="metric-value">{formatDuration(metrics.cacheAgeSeconds)}</div>
              <div className="metric-description">Cache Age</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                Refresh: {formatDuration(metrics.refreshAgeSeconds)}<br/>
                Backfill: {formatDuration(metrics.backfillAgeSeconds)}
              </div>
            </div>

            {/* Seed Status */}
            <div className="metric-card">
              <h3>Seed Status</h3>
              <div className="metric-value" style={{ color: metrics.seedReady ? '#48bb78' : '#ed8936' }}>
                {metrics.seedReady ? 'Ready' : 'Not Ready'}
              </div>
              <div className="metric-description">Seed State</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                Time: {formatDuration(metrics.seedTimeSeconds)}
              </div>
            </div>

            {/* Success/Error Counts */}
            <div className="metric-card">
              <h3>Refresh Operations</h3>
              <div className="metric-value">{formatNumber(metrics.refreshSuccessCount)}</div>
              <div className="metric-description">Success Count</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                Errors: {formatNumber(metrics.refreshErrorCount)}
              </div>
            </div>

            <div className="metric-card">
              <h3>Backfill Operations</h3>
              <div className="metric-value">{formatNumber(metrics.backfillSuccessCount)}</div>
              <div className="metric-description">Success Count</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                Errors: {formatNumber(metrics.backfillErrorCount)}<br/>
                Resume Height: {metrics.backfillResumeHeight === -1 ? 'None' : formatNumber(metrics.backfillResumeHeight)}
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="metric-card">
              <h3>GetBlocks Performance</h3>
              <div className="metric-value">{metrics.getBlocksP50Ms.toFixed(1)}ms</div>
              <div className="metric-description">P50 Latency</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                P90: {metrics.getBlocksP90Ms.toFixed(1)}ms<br/>
                P99: {metrics.getBlocksP99Ms.toFixed(1)}ms
              </div>
            </div>

            <div className="metric-card">
              <h3>GetBlockDetails Performance</h3>
              <div className="metric-value">{metrics.getBlockDetailsP50Ms.toFixed(1)}ms</div>
              <div className="metric-description">P50 Latency</div>
              <div style={{ marginTop: '1rem', fontSize: '0.9em', color: '#666' }}>
                P90: {metrics.getBlockDetailsP90Ms.toFixed(1)}ms<br/>
                P99: {metrics.getBlockDetailsP99Ms.toFixed(1)}ms
              </div>
            </div>
          </div>
        )}

        {!metrics && !loading && !error && (
          <div className="loading">No metrics data available</div>
        )}
      </div>
    </div>
  );
};

export default MetricsDashboard;
